import { type FC, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod/dist/zod.js'
import isEqual from 'lodash/isEqual'
import * as z from 'zod'

import { Body, Button, Divider, Dropdown, DropdownItem, generateRadixDropdownItem } from '@foxino/components-web'
import { ChevronDown, Save01 } from '@foxino/icons-react'

import { type GetUserProfileQuery, Language, type UserProfileInput } from '@app/data'
import { useTranslation } from '@app/locales'
import i18n, { languageCodeToSupportedLanguage } from '@app/locales/i18n'
import { languageToFlagIcon } from '@app/modules/common/utils/languageToFlagIcon'

type ProfileFormValues = UserProfileInput & {
  weekStart?: string
  timezone?: string
}

type Props = {
  userProfile: GetUserProfileQuery['userProfile']
  onSubmit: (data: UserProfileInput) => Promise<void>
}

export const GeneralForm: FC<Props> = ({ userProfile, onSubmit }: Props) => {
  const { t } = useTranslation(['settings', 'common'])

  const defaultValues = useMemo<ProfileFormValues>(() => {
    return {
      appLanguage: languageCodeToSupportedLanguage[i18n.language] || Language.ENGLISH
    }
  }, [userProfile])

  const {
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { dirtyFields }
  } = useForm<ProfileFormValues>({ defaultValues, resolver: zodResolver(validationSchema) })

  const appLanguage = useWatch({ control, name: 'appLanguage' })
  const Flag = languageToFlagIcon[appLanguage || Language.ENGLISH]
  const isTouched = !isEqual(dirtyFields, {}) || defaultValues?.appLanguage !== appLanguage

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex size-full flex-col p-5 text-white">
      <div className="flex items-center justify-between">
        <Body size="3xl" Component={'h3'} weight="bold">
          {t('preferences.title')}
        </Body>
        <div className="flex items-center gap-2">
          <Button color="secondary" onClick={() => reset()}>
            {t('cancel')}
          </Button>
          <Button LeadingIcon={Save01} disabled={!isTouched} type="submit">
            {t('save')}
          </Button>
        </div>
      </div>
      <Divider className="my-4 bg-primary-800" />
      {/* App Language */}
      <div className="mt-2 flex w-full flex-row items-center justify-between">
        <Body size="md" weight="semibold" className="w-[260px]">
          {t('preferences.language')}
        </Body>
        <Dropdown
          side="bottom"
          contentProps={{ className: 'z-[101] w-[200px]' }}
          items={[
            generateRadixDropdownItem({
              component: (
                <DropdownItem
                  label={t(`languages.${Language.CZECH.toLowerCase()}`)}
                  TrailingIcon={languageToFlagIcon[Language.CZECH]}
                  size="small"
                />
              ),
              onSelect: () => setValue('appLanguage', Language.CZECH)
            }),
            generateRadixDropdownItem({
              component: (
                <DropdownItem
                  label={t(`languages.${Language.ENGLISH.toLowerCase()}`)}
                  TrailingIcon={languageToFlagIcon[Language.ENGLISH]}
                  size="small"
                />
              ),
              onSelect: () => setValue('appLanguage', Language.ENGLISH)
            })
          ]}
        >
          <Button color="secondary" className="flex w-[200px] justify-between gap-2 border border-primary-700">
            <div className="flex items-center gap-2">
              {Flag && <Flag />}
              <Body size="md" weight="semibold">
                {t(`languages.${appLanguage?.toLowerCase()}`)}
              </Body>
            </div>
            <ChevronDown className="text-primary-300" />
          </Button>
        </Dropdown>
      </div>
    </form>
  )
}

const validationSchema = z.object({
  appLanguage: z.string()
})
